import type { NumericField } from '@talos/kyoko';
import type { GridApi, GridOptions, IRowNode } from 'ag-grid-community';
import type { ManualFillCareOrderBlotterRow } from './CareOrderManualFillsBlotter';

export function manualFillQtyInputID(rowID: string) {
  return `care-order-manual-fill-qty-input-${rowID}`;
}

export function manualFillPriceInputID(rowID: string) {
  return `care-order-manual-fill-price-input-${rowID}`;
}

export function manualFillAmountInputID(rowID: string) {
  return `care-order-manual-fill-amount-input-${rowID}`;
}

export const ManualFillInputColumn = {
  Qty: 'qty',
  Price: 'price',
  Amount: 'amount',
} as const;
export type ManualFillInputColumn = (typeof ManualFillInputColumn)[keyof typeof ManualFillInputColumn];

type ManualFillInputColumnConfig = {
  column: ManualFillInputColumn;
  colId: string;
  getInputID: (rowID: string) => string;
  getField: (row: ManualFillCareOrderBlotterRow) => NumericField;
};

type ManualFillInputPosition = {
  // Display order comes from AG Grid, not the DOM. Row index is only used to scroll
  // a virtualized row into view; rowID is the stable identity for finding the input.
  rowIndex: number;
  rowID: string;
  column: ManualFillInputColumnConfig;
};

const manualFillInputColumns: ManualFillInputColumnConfig[] = [
  {
    column: ManualFillInputColumn.Qty,
    colId: 'fillQty',
    getInputID: manualFillQtyInputID,
    getField: row => row.fillQty,
  },
  {
    column: ManualFillInputColumn.Price,
    colId: 'fillPrice',
    getInputID: manualFillPriceInputID,
    getField: row => row.fillPrice,
  },
  {
    column: ManualFillInputColumn.Amount,
    colId: 'fillAmount',
    getInputID: manualFillAmountInputID,
    getField: row => row.fillAmount,
  },
];

/**
 * AG Grid calls this when focus enters the grid from outside. Reuse the same
 * position model as tab navigation so entering from the top/bottom also respects
 * tree-data row order and disabled in-kind fields.
 */
export function createCareOrderManualFillsFocusGridInnerElementHandler(): NonNullable<
  GridOptions<ManualFillCareOrderBlotterRow>['focusGridInnerElement']
> {
  return params => {
    const positions = getManualFillInputPositions(params.api);
    const position = params.fromBottom ? positions.at(-1) : positions[0];

    if (!position) {
      return false;
    }

    return focusManualFillInputPosition({
      ownerDocument: document,
      gridApi: params.api,
      position,
    });
  };
}

/**
 * AG Grid tree-data and row virtualization can keep row DOM nodes mounted in an
 * order that does not match the visual row order. Native DOM tabbing then jumps
 * unpredictably, especially around expanded leg rows. Build a logical tab path
 * from AG Grid's displayed rows instead.
 */
export function focusNextManualFillInput({
  currentInput,
  direction,
  gridApi,
  node,
  column,
}: {
  currentInput: HTMLInputElement;
  direction: 'forward' | 'backward';
  gridApi: GridApi<ManualFillCareOrderBlotterRow>;
  node: IRowNode<ManualFillCareOrderBlotterRow>;
  column: ManualFillInputColumn;
}) {
  const rowData = node.data;
  if (rowData == null) {
    return false;
  }

  const positions = getManualFillInputPositions(gridApi);
  const currentIndex = positions.findIndex(
    position => position.rowID === rowData.rowID && position.column.column === column
  );
  if (currentIndex === -1) {
    return false;
  }

  const nextPosition = positions[currentIndex + (direction === 'forward' ? 1 : -1)];
  if (!nextPosition) {
    return false;
  }

  return focusManualFillInputPosition({
    ownerDocument: currentInput.ownerDocument,
    gridApi,
    position: nextPosition,
  });
}

/**
 * Returns the enabled manual-fill inputs in the same order the user sees them.
 * Cash rows expose qty/price/amount; in-kind rows only expose qty because price
 * and amount are disabled.
 */
function getManualFillInputPositions(gridApi: GridApi<ManualFillCareOrderBlotterRow>) {
  const positions: ManualFillInputPosition[] = [];

  for (let rowIndex = 0; rowIndex < gridApi.getDisplayedRowCount(); rowIndex++) {
    const row = gridApi.getDisplayedRowAtIndex(rowIndex)?.data;
    if (!row) {
      continue;
    }

    manualFillInputColumns.forEach(column => {
      if (!column.getField(row).isDisabled) {
        positions.push({ rowIndex, rowID: row.rowID, column });
      }
    });
  }

  return positions;
}

function focusManualFillInputPosition({
  ownerDocument,
  gridApi,
  position,
}: {
  ownerDocument: Document;
  gridApi: GridApi<ManualFillCareOrderBlotterRow>;
  position: ManualFillInputPosition;
}) {
  const inputID = position.column.getInputID(position.rowID);
  const focusInput = () => {
    const input = ownerDocument.getElementById(inputID);
    if (!(input instanceof HTMLInputElement)) {
      return false;
    }

    input.focus();
    input.select();
    return true;
  };

  if (focusInput()) {
    return true;
  }

  // The next logical input can be outside the currently rendered viewport. Ask
  // AG Grid to render it, then focus once the browser has painted the new row.
  gridApi.ensureIndexVisible(position.rowIndex);
  gridApi.ensureColumnVisible(position.column.colId);
  requestAnimationFrame(focusInput);
  return true;
}
